import React, { useEffect, useState } from "react";
import { X, Maximize2, Download, FileText } from "lucide-react";
import Layout from "../components/Layout";
import { useLang, localized } from "../lib/i18n";
import { api, FILE_URL } from "../lib/api";

// Small "peek" (no toolbar) for the card thumbnail.
const peekUrl = (fileId) => `${FILE_URL(fileId)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
// Full viewer (with toolbar) for the fullscreen modal.
const viewerUrl = (fileId) => `${FILE_URL(fileId)}#toolbar=1&navpanes=0&view=FitH`;

export default function PdfCatalogs() {
  const { t, lang } = useLang();
  const [catalogs, setCatalogs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/catalogs").then((r) => setCatalogs(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <section className="fd-container pt-12 md:pt-20 pb-8" data-testid="pdfs-header">
        <p className="fd-label mb-4">— {t("nav.pdfs")}</p>
        <h1 className="text-4xl md:text-6xl font-light tracking-tighter text-neutral-900">{t("pdfs.title")}</h1>
        <p className="mt-4 text-neutral-500 max-w-md">{t("pdfs.subtitle")}</p>
      </section>

      <section className="fd-container pb-16 md:pb-24" data-testid="pdfs-list">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (<div key={i} className="h-80 bg-neutral-100 animate-pulse border border-neutral-200" />))}
          </div>
        ) : catalogs.length === 0 ? (
          <div className="border border-dashed border-neutral-200 py-24 text-center text-sm text-neutral-400" data-testid="pdfs-empty">{t("pdfs.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {catalogs.map((c) => (
              <div key={c.id} className="border border-neutral-200 fd-hover-lift group" data-testid={`pdf-item-${c.id}`}>
                {/* Collapsed peek — click to open fully */}
                <button
                  onClick={() => setPreview(c)}
                  className="relative block w-full h-64 bg-neutral-100 overflow-hidden"
                  data-testid={`pdf-open-${c.id}`}
                  aria-label={localized(c.title, lang)}
                >
                  {c.cover_id ? (
                    <img
                      src={FILE_URL(c.cover_id)}
                      alt={localized(c.title, lang)}
                      className="absolute inset-0 w-full h-full object-cover"
                      data-testid={`pdf-cover-${c.id}`}
                    />
                  ) : (
                    <iframe
                      src={peekUrl(c.file_id)}
                      title={localized(c.title, lang)}
                      tabIndex={-1}
                      scrolling="no"
                      className="absolute top-0 left-0 w-full h-[440px] border-0 pointer-events-none"
                      data-testid={`pdf-peek-${c.id}`}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-60" />
                  <div className="absolute inset-0 flex items-center justify-center bg-neutral-900/25 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-neutral-900 text-xs uppercase tracking-widest">
                      <Maximize2 size={14} /> {t("pdfs.fullscreen")}
                    </span>
                  </div>
                </button>

                {/* Footer */}
                <div className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="fd-label mb-0.5">PDF</p>
                    <h3 className="text-neutral-900 truncate" data-testid={`pdf-title-${c.id}`}>{localized(c.title, lang)}</h3>
                  </div>
                  <a
                    href={FILE_URL(c.file_id)}
                    download
                    target="_blank" rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 border border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 shrink-0 transition-colors"
                    title={t("pdfs.download")}
                    data-testid={`pdf-download-${c.id}`}
                  >
                    <Download size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Fullscreen modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 md:p-8" onClick={() => setPreview(null)} data-testid="pdf-preview-modal">
          <div className="bg-white w-full max-w-6xl h-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-neutral-200">
              <p className="text-sm font-medium text-neutral-900 truncate">{localized(preview.title, lang)}</p>
              <div className="flex items-center gap-2">
                <a href={FILE_URL(preview.file_id)} download target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-neutral-900 text-white" data-testid="pdf-modal-download">
                  <Download size={14} /> {t("pdfs.download")}
                </a>
                <button onClick={() => setPreview(null)} className="p-2 hover:bg-neutral-100 rounded" data-testid="pdf-modal-close" aria-label={t("pdfs.close")}>
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-neutral-100">
              <iframe
                src={viewerUrl(preview.file_id)}
                title={localized(preview.title, lang)}
                className="w-full h-full border-0"
                data-testid="pdf-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
